import axios from "axios";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import ConstList from "../utils/ConstList";
import { maintenanceState } from "../stores/MaintenanceAtom";
import { LoginUserAtom } from "../stores/LoginUserAtom";

/**
 * master_authority上の管理者権限ID。
 */
const ADMIN_AUTHORITY_ID = 1;

/**
 * メンテナンス設定をAPIから再取得する間隔。
 * 5分ごと。
 */
const MAINTENANCE_FETCH_INTERVAL = 5 * 60 * 1000;

/**
 * 現在時刻を再判定する間隔。
 * API通信は行わず、ブラウザ内で1分ごとに更新する。
 */
const CURRENT_TIME_INTERVAL = 60 * 1000;

type Props = {
  children: ReactNode;
};

type ProfileResponse = {
  success?: boolean;
  message?: string;
  data?: {
    user_id?: number | string;
    email?: string;
    name?: string;
    authority_id?: number | string;
    authority_name?: string;
  } | null;
};

type MaintenanceResponse = {
  id?: number | string;
  display?: number | string | boolean;
  day?: string;
  start_time?: string;
  end_time?: string;
  flag?: number | string | boolean;
};

function MaintenanceGate({ children }: Props) {
  const [maintenance, setMaintenance] =
    useRecoilState(maintenanceState);

  const loginUserData = useRecoilValue(LoginUserAtom);

  const [currentTime, setCurrentTime] = useState<Date>(
    () => new Date()
  );

  const [maintenanceLoaded, setMaintenanceLoaded] =
    useState(false);

  const [authorityId, setAuthorityId] =
    useState<number | null>(null);

  const [authorityLoading, setAuthorityLoading] =
    useState(false);

  /**
   * メンテナンス設定取得。
   */
  const loadMaintenance = useCallback(async () => {
    try {
      const response = await axios.get<MaintenanceResponse>(
        ConstList.getApiUrl() + "GetMaintenance.php",
        ConstList.AXIOS_CONFIG
      );

      const responseData = response.data;

      if (
        !responseData ||
        typeof responseData !== "object"
      ) {
        throw new Error(
          "メンテナンス情報の応答形式が正しくありません。"
        );
      }

      setMaintenance(
        responseData as typeof maintenance
      );
    } catch (caughtError) {
      if (axios.isAxiosError(caughtError)) {
        console.error(
          "メンテナンス情報の取得に失敗しました。",
          {
            status: caughtError.response?.status,
            responseData: caughtError.response?.data,
            message: caughtError.message,
          }
        );
      } else {
        console.error(
          "メンテナンス情報の取得に失敗しました。",
          caughtError
        );
      }
    } finally {
      setMaintenanceLoaded(true);
    }
  }, [setMaintenance]);

  /**
   * 初回取得と5分ごとの再取得。
   */
  useEffect(() => {
    void loadMaintenance();

    const intervalId = window.setInterval(() => {
      void loadMaintenance();
    }, MAINTENANCE_FETCH_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadMaintenance]);

  /**
   * 現在時刻を1分ごとに更新。
   */
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, CURRENT_TIME_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  /**
   * Auth0ログインユーザーのメールアドレスから、
   * DB上の権限を取得する。
   */
  useEffect(() => {
    let mounted = true;

    const loadAuthority = async () => {
      const email =
        loginUserData.email?.trim() ?? "";

      if (!email) {
        if (mounted) {
          setAuthorityId(null);
          setAuthorityLoading(false);
        }

        return;
      }

      try {
        setAuthorityLoading(true);

        const response =
          await axios.post<ProfileResponse>(
            ConstList.getApiUrl() +
              "GetMyProfile.php",
            {
              email,
            },
            ConstList.AXIOS_CONFIG
          );

        if (!mounted) {
          return;
        }

        const profile = response.data?.data;

        if (!profile) {
          setAuthorityId(null);
          return;
        }

        setAuthorityId(
          Number(profile.authority_id ?? 0)
        );
      } catch (caughtError) {
        console.error(
          "ログインユーザーの権限確認に失敗しました。",
          caughtError
        );

        if (mounted) {
          setAuthorityId(null);
        }
      } finally {
        if (mounted) {
          setAuthorityLoading(false);
        }
      }
    };

    void loadAuthority();

    return () => {
      mounted = false;
    };
  }, [loginUserData.email]);

  /**
   * 現在がメンテナンス対象時間内か判定。
   */
  const isMaintenanceMode = useMemo(() => {
    return isWithinMaintenanceWindow(
      maintenance,
      currentTime
    );
  }, [maintenance, currentTime]);

  const isAdministrator =
    authorityId === ADMIN_AUTHORITY_ID;

  if (!maintenanceLoaded) {
    return (
      <LoadingScreen message="システム情報を確認しています。" />
    );
  }

  /**
   * メンテナンス時間外。
   */
  if (!isMaintenanceMode) {
    return <>{children}</>;
  }

  /**
   * メンテナンス中は権限確認完了まで待つ。
   */
  if (authorityLoading) {
    return (
      <LoadingScreen message="ユーザー権限を確認しています。" />
    );
  }

  /**
   * 管理者は通常画面を利用可能。
   * ただし画面上部へ固定警告を表示する。
   */
  if (isAdministrator) {
    return (
      <>
        <AdminMaintenanceNotice
          day={maintenance?.day}
          startTime={maintenance?.start_time}
          endTime={maintenance?.end_time}
          onReload={() => {
            void loadMaintenance();
            setCurrentTime(new Date());
          }}
        />

        {children}
      </>
    );
  }

  /**
   * 一般ユーザーにはメンテナンス画面を表示。
   */
  return (
    <MaintenanceScreen
      day={maintenance?.day}
      startTime={maintenance?.start_time}
      endTime={maintenance?.end_time}
      onReload={() => {
        void loadMaintenance();
        setCurrentTime(new Date());
      }}
    />
  );
}

/**
 * 管理者向けのメンテナンス警告。
 *
 * 通常画面を操作できる状態のまま、
 * 画面上部へ固定表示する。
 */
type AdminMaintenanceNoticeProps = {
  day?: string;
  startTime?: string;
  endTime?: string;
  onReload: () => void;
};

function AdminMaintenanceNotice({
  day,
  startTime,
  endTime,
  onReload,
}: AdminMaintenanceNoticeProps) {
  return (
    <Box
      sx={{
        position: "fixed",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        width: {
          xs: "calc(100% - 24px)",
          md: "auto",
        },
        minWidth: {
          md: 600,
        },
        maxWidth: 900,
        zIndex: (theme) =>
          theme.zIndex.snackbar + 1,
      }}
    >
      <Alert
        severity="warning"
        variant="filled"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={onReload}
            sx={{
              whiteSpace: "nowrap",
              fontWeight: 700,
            }}
          >
            状態確認
          </Button>
        }
        sx={{
          borderRadius: 2,
          boxShadow: 4,
          alignItems: "center",
        }}
      >
        <AlertTitle sx={{ fontWeight: 800 }}>
          現在メンテナンス中です
        </AlertTitle>

        管理者としてアクセスしています。一般ユーザーは現在利用できません。
        {"　"}
        実施日時：{day || "未定"}{" "}
        {formatTime(startTime)}～
        {formatTime(endTime)}
      </Alert>
    </Box>
  );
}

/**
 * flagがONで、現在時刻が設定期間内ならtrue。
 */
function isWithinMaintenanceWindow(
  maintenance: MaintenanceResponse,
  currentTime: Date
): boolean {
  const maintenanceFlag =
    Number(maintenance?.flag) === 1;

  if (!maintenanceFlag) {
    return false;
  }

  const day = maintenance?.day ?? "";
  const startTime =
    maintenance?.start_time ?? "";
  const endTime =
    maintenance?.end_time ?? "";

  const startDate = createLocalDateTime(
    day,
    startTime
  );

  const endDate = createLocalDateTime(
    day,
    endTime
  );

  if (!startDate || !endDate) {
    return false;
  }

  return (
    currentTime.getTime() >=
      startDate.getTime() &&
    currentTime.getTime() < endDate.getTime()
  );
}

/**
 * YYYY-MM-DDとHH:mm:ssからローカル日時を作成。
 */
function createLocalDateTime(
  day: string,
  time: string
): Date | null {
  const dayMatch =
    /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);

  const timeMatch =
    /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(
      time
    );

  if (!dayMatch || !timeMatch) {
    return null;
  }

  const year = Number(dayMatch[1]);
  const month = Number(dayMatch[2]);
  const date = Number(dayMatch[3]);

  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  const second = Number(timeMatch[3] ?? 0);

  if (
    month < 1 ||
    month > 12 ||
    date < 1 ||
    date > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return null;
  }

  const result = new Date(
    year,
    month - 1,
    date,
    hour,
    minute,
    second
  );

  if (
    result.getFullYear() !== year ||
    result.getMonth() !== month - 1 ||
    result.getDate() !== date
  ) {
    return null;
  }

  return result;
}

function formatTime(value: unknown): string {
  if (
    typeof value !== "string" ||
    value.trim() === ""
  ) {
    return "未定";
  }

  const parts = value.split(":");

  if (parts.length < 2) {
    return value;
  }

  return `${parts[0]}:${parts[1]}`;
}

type LoadingScreenProps = {
  message: string;
};

function LoadingScreen({
  message,
}: LoadingScreenProps) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack spacing={2} alignItems="center">
        <CircularProgress />

        <Typography color="text.secondary">
          {message}
        </Typography>
      </Stack>
    </Box>
  );
}

type MaintenanceScreenProps = {
  day?: string;
  startTime?: string;
  endTime?: string;
  onReload: () => void;
};

function MaintenanceScreen({
  day,
  startTime,
  endTime,
  onReload,
}: MaintenanceScreenProps) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.100",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: {
              xs: 3,
              sm: 5,
            },
            borderRadius: 3,
          }}
        >
          <Stack spacing={3}>
            <Alert severity="warning">
              <AlertTitle>
                システムメンテナンス中です
              </AlertTitle>

              現在、システムをご利用いただけません。
            </Alert>

            <Stack spacing={1}>
              <Typography>
                実施日：{day || "未定"}
              </Typography>

              <Typography>
                予定時間：
                {formatTime(startTime)}
                ～
                {formatTime(endTime)}
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              メンテナンス終了後、最大1分程度で通常画面へ戻ります。
            </Typography>

            <Button
              variant="outlined"
              onClick={onReload}
            >
              メンテナンス状態を再確認
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default MaintenanceGate;